import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Download, Loader2, LogIn, PackageOpen, ArrowRight, Clock3, BadgeCheck } from "lucide-react";
import { AeroNav } from "@/components/aero/AeroNav";
import { AeroFooter } from "@/components/aero/AeroFooter";
import { useAuth } from "@/hooks/useAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

export default function MyOrdersPage() {
  const { user, login } = useAuth();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("session_token");
    axios
      .get(`${API}/my-orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]));
  }, [user]);

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-[#F8F6F2] text-[#111827] min-h-screen"
      data-testid="my-orders-page"
    >
      <AeroNav />
      <section className="max-w-[1000px] mx-auto px-6 md:px-10 pt-36 pb-24 md:pt-44">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }}>
          <p className="font-mono text-[14px] tracking-[0.3em] uppercase text-[#4F6D8A] flex items-center gap-4">
            <span className="w-10 h-px bg-[#4F6D8A]/60 inline-block" /> Purchase History
          </p>
          <h1 className="mt-4 font-serif text-[40px] md:text-[56px] leading-[1.02] text-[#111827]">My Orders</h1>
          <p className="mt-4 text-[15px] text-[#6B7280] leading-relaxed max-w-xl">
            Apne saare purchases ek jagah — kabhi bhi dobara download karo, link kabhi expire nahi hota.
          </p>
        </motion.div>

        {/* signed out */}
        {user === null && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="mt-14 bg-white rounded-[26px] border border-[#E5E7EB] shadow-[0_35px_80px_-30px_rgba(15,42,67,0.25)] p-10 md:p-14 text-center"
            data-testid="my-orders-signin"
          >
            <span className="mx-auto w-16 h-16 rounded-full bg-[#0F2A43] flex items-center justify-center">
              <LogIn size={26} className="text-white" />
            </span>
            <h2 className="mt-7 font-serif text-3xl text-[#111827]">Sign in to continue</h2>
            <p className="mt-3 text-[#6B7280] text-[15px] leading-relaxed max-w-sm mx-auto">
              Apne orders dekhne ke liye usi Google account se sign in karo jisse purchase kiya tha.
            </p>
            <button
              data-testid="my-orders-google-btn"
              onClick={login}
              className="mt-8 inline-flex items-center gap-3 bg-white border border-[#E5E7EB] text-[#111827] font-mono text-[12px] font-bold tracking-[0.16em] uppercase px-8 py-4 rounded-full shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
            >
              <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2.1 3.7-5.1 3.7-8.6z"/><path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.2-6.9-5.1l-3.8 3C3.4 21.3 7.4 24 12 24z"/><path fill="#FBBC05" d="M5.1 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3l-3.8-3C.5 8.2 0 10 0 12s.5 3.8 1.3 5.3l3.8-3z"/><path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.4 0 3.4 2.7 1.3 6.7l3.8 3c1-3 3.7-5 6.9-5z"/></svg>
              Continue with Google
            </button>
          </motion.div>
        )}

        {/* loading */}
        {(user === undefined || (user && orders === null)) && (
          <div className="mt-20 flex flex-col items-center" data-testid="my-orders-loading">
            <Loader2 size={28} className="animate-spin text-[#4F6D8A]" />
            <p className="mt-4 font-mono text-[11px] tracking-[0.3em] uppercase text-[#6B7280]">Loading your orders…</p>
          </div>
        )}

        {/* orders list */}
        {user && orders !== null && (
          <div className="mt-12 space-y-5" data-testid="my-orders-list">
            {orders.length === 0 && (
              <div className="bg-white rounded-[26px] border border-[#E5E7EB] p-12 text-center" data-testid="my-orders-empty">
                <PackageOpen size={34} strokeWidth={1.2} className="mx-auto text-[#9CA3AF]" />
                <p className="mt-5 font-serif text-2xl text-[#111827]">Abhi koi purchase nahi</p>
                <p className="mt-2 text-sm text-[#6B7280]">Products page se kuch bhi kharido — yahan dikhega.</p>
                <Link
                  to="/products"
                  data-testid="my-orders-browse"
                  className="mt-7 inline-flex items-center gap-2 bg-[#0F2A43] text-white font-mono text-[11px] font-bold tracking-[0.2em] uppercase px-8 py-4 rounded-full hover:bg-[#4F6D8A] transition-colors"
                >
                  Browse Products <ArrowRight size={13} />
                </Link>
              </div>
            )}
            {orders.map((o) => (
              <motion.div
                key={o.purchase_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease }}
                data-testid={`my-order-${o.purchase_id}`}
                className="bg-white rounded-[22px] border border-[#E5E7EB] p-6 md:p-7 flex flex-wrap items-center gap-x-6 gap-y-4 shadow-[0_10px_30px_-15px_rgba(15,42,67,0.12)]"
              >
                <div className="flex-1 min-w-[200px]">
                  <p className="font-display font-bold text-lg text-[#111827]">{o.product_name}</p>
                  <p className="mt-1 font-mono text-[11px] tracking-wide text-[#9CA3AF]">
                    {o.purchase_id} · {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center gap-1.5 font-mono text-[10px] font-bold tracking-[0.18em] uppercase px-4 py-2 rounded-full border ${o.status === "paid" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}
                  data-testid={`my-order-status-${o.purchase_id}`}
                >
                  {o.status === "paid" ? <BadgeCheck size={12} /> : <Clock3 size={12} />} {o.status}
                </span>
                <span className="font-display font-black text-lg text-[#0F2A43]">₹{(o.amount / 100).toLocaleString("en-IN")}</span>
                {o.download_url ? (
                  <a
                    href={o.download_url}
                    data-testid={`my-order-download-${o.purchase_id}`}
                    className="inline-flex items-center gap-2 bg-[#0F2A43] text-white font-mono text-[10px] font-bold tracking-[0.2em] uppercase px-6 py-3.5 rounded-full hover:bg-[#4F6D8A] hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <Download size={13} /> Download
                  </a>
                ) : (
                  o.status === "paid" && (
                    <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#9CA3AF]" data-testid={`my-order-pending-file-${o.purchase_id}`}>
                      File email par aa rahi hai
                    </span>
                  )
                )}
              </motion.div>
            ))}
          </div>
        )}
      </section>
      <AeroFooter />
    </motion.main>
  );
}
